"use client";

import ArrowLeftIcon from "@/app/_components/Icons/ArrowLeftIcon";
import { constants } from "@/environment/constants";
import { Box, Card, CardActionArea, Icon, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import Image from "next/image";
import NextLink from "next/link";

const HREF = "/products?sort=new&utm_source=zibasi&utm_medium=banner";

const badgeBob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const ctaPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(123, 63, 228, 0.35); }
  50% { box-shadow: 0 0 0 8px rgba(123, 63, 228, 0); }
`;

const floatPortrait = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const floatOrb = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-10px, 8px) scale(1.06); }
`;

type Props = {
  variant: "desktop" | "mobile";
};

export default function NewArrivalsBentoCard({ variant }: Props) {
  const isDesktop = variant === "desktop";

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        height: isDesktop ? "100%" : 188,
        minHeight: isDesktop ? 220 : undefined,
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        // Keep inner z-index (text/portrait) from competing with SiteHeader (zIndex: 3)
        isolation: "isolate",
        background: "linear-gradient(135deg, #F8F2FF 0%, #EFE4FC 45%, #E4D2FA 100%)",
      }}
    >
      <CardActionArea
        component={NextLink}
        href={HREF}
        aria-label="مشاهده جدیدترین محصولات"
        sx={{
          width: "100%",
          height: "100%",
          display: "block",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            insetInlineEnd: isDesktop ? "-8%" : "0%",
            top: isDesktop ? "-18%" : "-40%",
            width: isDesktop ? "58%" : "55%",
            aspectRatio: "1",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(167, 112, 240, 0.3) 0%, rgba(167, 112, 240, 0) 70%)",
            animation: `${floatOrb} 7s ease-in-out infinite`,
            zIndex: 0,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />

        {/* Portrait — cutout like hero; face large, bottom-anchored */}
        <Box
          sx={{
            position: "absolute",
            insetInlineEnd: isDesktop ? "-6%" : "-8%",
            bottom: isDesktop ? "0%" : "-8%",
            // Pull empty headroom of the cutout above the clip so face reads large
            top: isDesktop ? "-12%" : "-34%",
            width: isDesktop ? "62%" : "62%",
            zIndex: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              animation: `${floatPortrait} 5.5s ease-in-out infinite`,
              filter: "drop-shadow(0 16px 26px rgba(90, 40, 140, 0.16))",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Image
              src="/main/4.webp"
              alt=""
              fill
              priority={!isDesktop}
              draggable={false}
              sizes={isDesktop ? "(max-width: 1200px) 32vw, 340px" : "55vw"}
              style={{
                // contain keeps the cutout intact; bottom aligns face large in frame
                objectFit: "contain",
                objectPosition: "bottom center",
                userSelect: "none",
              }}
            />
          </Box>
        </Box>

        {/* Scrim — authored LTR (stylis flips for RTL text side) */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background: isDesktop
              ? "linear-gradient(to right, rgba(247, 240, 255, 0.97) 0%, rgba(247, 240, 255, 0.88) 30%, rgba(247, 240, 255, 0.25) 50%, transparent 64%)"
              : "linear-gradient(to right, rgba(247, 240, 255, 0.97) 0%, rgba(247, 240, 255, 0.9) 38%, rgba(247, 240, 255, 0.2) 55%, transparent 70%)",
          }}
        />

        <Stack
          justifyContent="center"
          spacing={isDesktop ? 1.05 : 0.55}
          sx={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            width: isDesktop ? "48%" : "50%",
            p: isDesktop ? 2.75 : 2,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              alignSelf: "flex-start",
              px: isDesktop ? 1.35 : 1.1,
              py: isDesktop ? 0.45 : 0.35,
              borderRadius: 999,
              bgcolor: "#7B3FE4",
              color: "#fff",
              fontWeight: 800,
              fontSize: isDesktop ? "0.78rem" : "0.72rem",
              lineHeight: 1.2,
              animation: `${badgeBob} 2.6s ease-in-out infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            جدید
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              color: "grey.900",
              lineHeight: 1.15,
              fontSize: isDesktop ? "1.5rem" : "1.25rem",
            }}
          >
            به روز باش!
          </Typography>

          <Typography
            sx={{
              color: "grey.700",
              lineHeight: 1.4,
              fontSize: isDesktop ? "0.92rem" : "0.82rem",
              maxWidth: isDesktop ? 180 : 160,
            }}
          >
            جدیدترین‌های {constants.siteName}
            <br />
            همین الان ببین
          </Typography>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              alignSelf: "flex-start",
              gap: 0.55,
              mt: 0.15,
              px: isDesktop ? 1.9 : 1.4,
              py: isDesktop ? 0.85 : 0.65,
              borderRadius: 300,
              bgcolor: "#7B3FE4",
              color: "#fff",
              animation: `${ctaPulse} 2.5s ease-in-out infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: isDesktop ? "0.88rem" : "0.8rem", lineHeight: 1, color: "inherit" }}>
              مشاهده همه
            </Typography>
            <Icon sx={{ color: "inherit", fontSize: isDesktop ? 18 : 16, width: isDesktop ? 18 : 16, height: isDesktop ? 18 : 16 }}>
              <ArrowLeftIcon />
            </Icon>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
